import { useEffect, useRef, useState } from "react";
import { ChevronDown, Globe, Menu, X, ArrowRight } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

// ─── Constants ────────────────────────────────────────────────────────────────

const navItems = [
  { name: "Home", href: "/", isHash: false },
  { name: "About", href: "#about", isHash: true },
  { name: "Philosophy", href: "#philosophy", isHash: true },
  { name: "Ecosystem", href: "#ecosystem", isHash: true },
  { name: "Journal", href: "#journal", isHash: true },
  { name: "Contact", href: "#contact", isHash: true },
];

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "ta", label: "Tamil" },
  { code: "hi", label: "Hindi" },
];

const GT_SCRIPT_ID  = "google-translate-script";
const GT_ELEMENT_ID = "google_translate_element";
const GT_TIMEOUT    = 6000; // Keep a slow third-party script from blocking the selector too long.
const GT_HIDDEN_SELECTORS = [
  ".goog-te-banner-frame",
  ".goog-te-balloon-frame",
  "#goog-gt-tt",
  ".goog-tooltip",
  ".skiptranslate > span",
  "iframe.skiptranslate",
  ".skiptranslate iframe",
  ".VIpgJd-ZVi9od-ORHb-OEVmcd",
  ".VIpgJd-ZVi9od-aZ2wEe",
  ".VIpgJd-ZVi9od-aZ2wEe-wOHMyf",
  ".VIpgJd-yAWNEb-VIpgJd-fmcmS-sn54Q",
].join(", ");

// ─── WhatsApp Icon ─────────────────────────────────────────────────────────────

const WhatsAppIcon = ({ size = 18, color = "#fff" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"
    width={size} height={size} fill={color} style={{ flexShrink: 0 }}>
    <path d="M16 0C7.163 0 0 7.163 0 16c0 2.822.736 5.471 2.027 7.773L0 32l8.454-2.01A15.938 15.938 0 0 0 16 32c8.837 0 16-7.163 16-16S24.837 0 16 0zm0 29.333a13.27 13.27 0 0 1-6.748-1.838l-.484-.287-5.017 1.194 1.215-4.888-.316-.502A13.267 13.267 0 0 1 2.667 16C2.667 8.636 8.636 2.667 16 2.667S29.333 8.636 29.333 16 23.364 29.333 16 29.333zm7.27-9.592c-.398-.199-2.355-1.162-2.72-1.295-.366-.133-.632-.199-.898.199-.266.398-1.03 1.295-1.263 1.561-.233.266-.466.299-.864.1-.398-.199-1.681-.619-3.202-1.977-1.183-1.056-1.982-2.361-2.214-2.759-.233-.398-.025-.613.175-.811.18-.178.398-.466.597-.699.199-.233.266-.398.398-.664.133-.266.067-.499-.033-.698-.1-.199-.898-2.165-1.23-2.963-.324-.778-.654-.673-.898-.686l-.765-.013c-.266 0-.698.1-1.063.499-.366.398-1.396 1.362-1.396 3.322 0 1.96 1.43 3.854 1.629 4.12.199.266 2.814 4.297 6.818 6.027.953.411 1.696.657 2.275.841.956.305 1.826.262 2.514.159.767-.114 2.355-.963 2.688-1.893.332-.93.332-1.728.233-1.893-.1-.166-.366-.266-.764-.465z" />
  </svg>
);

// ─── Cookie helpers ────────────────────────────────────────────────────────────

function getCookieDomain() {
  const h = window.location.hostname;
  return h && h !== "localhost" ? h : "";
}

function setGoogCookie(value) {
  document.cookie = `googtrans=${value}; path=/`;
  const d = getCookieDomain();
  if (d) document.cookie = `googtrans=${value}; path=/; domain=${d}`;
}

function clearGoogCookie() {
  document.cookie = "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";
  const d = getCookieDomain();
  if (d) document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${d}`;
}

function getActiveLanguageFromCookie() {
  const match = document.cookie.match(/(?:^|;\s*)googtrans=\/[^/]+\/([^;]+)/);
  const code = match?.[1];
  return LANGUAGES.some((l) => l.code === code) ? code : "en";
}

// ─── Google Translate bootstrap ───────────────────────────────────────────────

function hideGoogleTranslateChrome() {
  document.querySelectorAll(GT_HIDDEN_SELECTORS).forEach((node) => {
    node.style.setProperty("display", "none", "important");
    node.style.setProperty("visibility", "hidden", "important");
  });

  document.querySelectorAll(".goog-text-highlight").forEach((node) => {
    node.style.setProperty("background-color", "transparent", "important");
    node.style.setProperty("box-shadow", "none", "important");
  });

  document.documentElement.style.setProperty("top", "0px", "important");
  document.documentElement.style.setProperty("margin-top", "0px", "important");
  document.body.style.setProperty("top", "0px", "important");
  document.body.style.setProperty("margin-top", "0px", "important");
}

let _gtReadyPromise = null;

function ensureGTReady() {
  const existing = document.querySelector(".goog-te-combo");
  if (existing) return Promise.resolve(existing);

  if (_gtReadyPromise) return _gtReadyPromise;

  _gtReadyPromise = new Promise((resolve, reject) => {
    let done = false;

    const finish = (err) => {
      if (done) return;
      done = true;
      clearInterval(pollId);
      clearTimeout(capId);
      if (err) { _gtReadyPromise = null; reject(err); }
    };

    const pollId = setInterval(() => {
      const combo = document.querySelector(".goog-te-combo");
      if (combo) {
        hideGoogleTranslateChrome();
        finish(null);
        resolve(combo);
      }
    }, 150);

    const capId = setTimeout(() => {
      finish(new Error("GT timeout"));
    }, GT_TIMEOUT);

    if (!document.getElementById(GT_SCRIPT_ID)) {
      window.googleTranslateElementInit = () => {
        const el = document.getElementById(GT_ELEMENT_ID);
        if (!el || el.dataset.initialized) return;
        el.dataset.initialized = "true";
        new window.google.translate.TranslateElement(
          {
            pageLanguage: "en",
            includedLanguages: LANGUAGES.map((l) => l.code).join(","),
            autoDisplay: false,
            layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
          },
          GT_ELEMENT_ID
        );
      };
      const s = document.createElement("script");
      s.id = GT_SCRIPT_ID;
      s.src = "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
      s.async = true;
      s.onerror = () => finish(new Error("GT script failed"));
      document.body.appendChild(s);
    }
  });

  return _gtReadyPromise;
}

function waitForTranslationToFinish(QUIET_MS = 250, MAX_MS = 4000) {
  return new Promise((resolve) => {
    let quietTimer = null;

    const done = () => {
      observer.disconnect();
      clearTimeout(capTimer);
      clearTimeout(quietTimer);
      resolve();
    };

    const observer = new MutationObserver(() => {
      clearTimeout(quietTimer);
      quietTimer = setTimeout(done, QUIET_MS);
    });

    observer.observe(document.documentElement, {
      subtree: true,
      childList: true,
      characterData: true,
    });

    const capTimer = setTimeout(done, MAX_MS);
    quietTimer = setTimeout(done, QUIET_MS);
  });
}

async function changeLanguage(langCode) {
  if (langCode === "en") {
    clearGoogCookie();
    window.location.reload();
    return;
  }

  setGoogCookie(`/en/${langCode}`);

  const combo = await ensureGTReady();

  combo.value = langCode;
  combo.dispatchEvent(new Event("change", { bubbles: true }));

  await waitForTranslationToFinish();
  hideGoogleTranslateChrome();
}

// ─── Spinner ──────────────────────────────────────────────────────────────────

const Spinner = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
    style={{ animation: "gtSpin 0.75s linear infinite", flexShrink: 0 }}>
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);

// ─── Desktop Language Switcher ────────────────────────────────────────────────

function LanguageSwitcher({ active, busy, onSelect }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const activeLabel = LANGUAGES.find((l) => l.code === active)?.label ?? "English";

  return (
    <div ref={ref} className="relative">
      <div id={GT_ELEMENT_ID} aria-hidden="true" style={{
        position: "absolute", left: "-9999px",
        width: "1px", height: "1px", overflow: "hidden",
        opacity: 0, pointerEvents: "none",
      }} />

      <button type="button"
        onClick={() => !busy && setOpen((o) => !o)}
        disabled={busy}
        className={`inline-flex w-[104px] items-center justify-between gap-2 rounded-sm border border-[#1a2e22]/50 px-2.5 py-1.5 text-[10px] tracking-[0.16em] uppercase font-bold transition-all duration-300 ${
          busy ? "opacity-70 cursor-wait" : "cursor-pointer"
        } text-[#1a2e22] hover:text-[#FCB042]`}
      >
        <span className="flex w-4 shrink-0 items-center justify-center">
          {busy ? <Spinner size={14} /> : <Globe size={14} className="text-[#1a2e22]/60" />}
        </span>
        <span className="min-w-0 flex-1 text-center whitespace-nowrap">{busy ? "Translating…" : activeLabel}</span>
        {!busy && (
          <ChevronDown size={14} className={`w-3.5 shrink-0 transition-transform duration-300 text-[#1a2e22]/40 ${open ? "rotate-180" : ""}`} />
        )}
      </button>

      <AnimatePresence>
        {open && !busy && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full left-1/2 z-50 mt-2 w-[124px] -translate-x-1/2 origin-top bg-white p-1.5 shadow-luxury border border-ayur-200"
          >
            {LANGUAGES.map((lang) => (
              <button key={lang.code} type="button"
                onClick={() => { setOpen(false); onSelect(lang.code); }}
                className={`flex min-h-9 w-full items-center justify-start gap-2 px-3 py-2 text-left text-xs uppercase tracking-[0.14em] leading-none transition-colors ${
                  active === lang.code 
                    ? "bg-ayur-50 text-ayur-950 font-bold" 
                    : "text-ayur-600 hover:bg-ayur-50 hover:text-ayur-950"
                }`}
              >
                <span className={`h-1 w-1 shrink-0 rounded-full ${active === lang.code ? "bg-ayur-400" : "bg-transparent"}`} />
                <span className="flex-1 text-left">{lang.label}</span>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Mobile Language Pill ─────────────────────────────────────────────────────

function MobileLangButton({ active, busy, lang, onSelect }) {
  const isActive = active === lang.code;
  return (
    <button type="button" onClick={() => onSelect(lang.code)} disabled={busy}
      className={`inline-flex min-w-[88px] items-center justify-center gap-1.5 px-4 py-2 text-xs uppercase tracking-widest font-medium transition-all duration-300 border ${
        isActive 
          ? "bg-ayur-950 text-white border-ayur-950" 
          : "bg-transparent text-ayur-700 border-ayur-200 hover:border-ayur-400"
      } ${busy ? "opacity-70 cursor-wait" : "cursor-pointer"}`}
    >
      {busy && isActive && <Spinner size={12} />}
      {lang.label}
    </button>
  );
}

// ─── Navbar ───────────────────────────────────────────────────────────────────

export default function Navbar() {
  const [menuOpen, setMenuOpen]     = useState(false);
  const [activeLang, setActiveLang] = useState(getActiveLanguageFromCookie);
  const [busy, setBusy]             = useState(false);
  const location  = useLocation();
  const navigate  = useNavigate();

  useEffect(() => {
    hideGoogleTranslateChrome();
    const observer = new MutationObserver(() => hideGoogleTranslateChrome());
    observer.observe(document.body, { childList: true });
    // Start loading the hidden Translate control after the first paint so the
    // first language change does not also wait for the external script.
    ensureGTReady().then(() => hideGoogleTranslateChrome()).catch(() => {});
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    hideGoogleTranslateChrome();
    if (activeLang === "en") return;
    ensureGTReady().then(() => hideGoogleTranslateChrome()).catch(() => {});
  }, [activeLang]);

  const handleHashClick = (e, href) => {
    e.preventDefault();
    const hash = href.split("#")[1];
    if (location.pathname === "/") {
      const el = document.getElementById(hash);
      if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 80, behavior: "smooth" });
    } else {
      navigate(`/#${hash}`);
    }
    setMenuOpen(false);
  };

  const handleLanguageSelect = async (langCode) => {
    if (busy || langCode === activeLang) return;
    setBusy(true);
    try {
      await changeLanguage(langCode);
      setActiveLang(langCode);
    } catch (err) {
      console.error("Translation failed:", err);
      window.location.reload();
    } finally {
      setBusy(false);
    }
  };

  const whatsappLink =
    "https://wa.me/9786424450?text=Hello%20Vijaya%20%F0%9F%91%8B%0A%0AI%20visited%20your%20portfolio%20website%20and%20contacted%20you.";

  return (
    <>
      <style>{`
        @keyframes gtSpin {
          to { transform: rotate(360deg); }
        }
        .goog-te-banner-frame, .goog-te-balloon-frame, #goog-gt-tt, .goog-tooltip, .skiptranslate > span, iframe.skiptranslate, .skiptranslate iframe, .VIpgJd-ZVi9od-ORHb-OEVmcd, .VIpgJd-ZVi9od-aZ2wEe, .VIpgJd-ZVi9od-aZ2wEe-wOHMyf, .VIpgJd-yAWNEb-VIpgJd-fmcmS-sn54Q { display: none !important; visibility: hidden !important; }
        html, body { top: 0 !important; margin-top: 0 !important; }
        .goog-te-gadget { font-size: 0 !important; }
        .goog-text-highlight { background-color: transparent !important; box-shadow: none !important; }
      `}</style>

      {/* Elegant Full-Width Nav */}
      <motion.nav 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-out bg-[#fdfcfb]/95 backdrop-blur-md border-b border-[#1a2e22]/5 py-3 shadow-[0_2px_15px_-3px_rgba(0,0,0,0.07)]`}
      >
        <div className="max-w-[90rem] mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between">

            {/* Brand */}
            <Link to="/" className="flex flex-col gap-0 group shrink-0">
              <span className="font-outfit text-xl font-black tracking-tighter leading-none text-[#1a2e22]">
                VASEEGRAH
              </span>
              <span className="text-[7px] font-bold uppercase tracking-[0.4em] leading-none mt-1.5 text-[#1a2e22]/50">
                Purely Natural - Purely Herbal
              </span>
            </Link>

            {/* Desktop Links */}
            <div className="hidden lg:flex items-center gap-8 xl:gap-10">
              {navItems.map((item) =>
                item.isHash ? (
                  <a key={item.name} href={item.href} onClick={(e) => handleHashClick(e, item.href)}
                    className="text-[10px] uppercase tracking-[0.2em] font-bold transition-all relative group hover:text-[#FCB042] text-[#1a2e22]">
                    {item.name}
                    <span className="absolute -bottom-1 left-0 w-0 h-[1px] bg-[#FCB042] transition-all group-hover:w-full" />
                  </a>
                ) : (
                  <Link key={item.name} to={item.href} onClick={() => setMenuOpen(false)}
                    className="text-[10px] uppercase tracking-[0.2em] font-bold transition-all relative group hover:text-[#FCB042] text-[#1a2e22]">
                    {item.name}
                    <span className="absolute -bottom-1 left-0 w-0 h-[1px] bg-[#FCB042] transition-all group-hover:w-full" />
                  </Link>
                )
              )}
            </div>

            {/* Right Actions */}
            <div className="hidden md:flex items-center gap-4 lg:gap-8">
              <LanguageSwitcher active={activeLang} busy={busy} onSelect={handleLanguageSelect} />

              <a href="#contact" onClick={(e) => handleHashClick(e, "#contact")}
                className="group inline-flex items-center gap-3 px-6 py-2.5 bg-[#FCB042] text-white text-[10px] uppercase tracking-[0.15em] font-bold hover:bg-[#e0992e] transition-all duration-300 rounded-full shadow-md">
                Contact Us <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
              </a>
            </div>

            {/* Mobile Toggle */}
            <button type="button" onClick={() => setMenuOpen(!menuOpen)}
              className={`lg:hidden p-2 transition-colors text-[#1a2e22]`}>
              {menuOpen ? <X size={28} strokeWidth={1.5} /> : <Menu size={28} strokeWidth={1.5} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="absolute top-full left-0 right-0 bg-white border-b border-ayur-100 overflow-hidden lg:hidden shadow-luxury"
            >
              <div className="px-6 py-8 flex flex-col gap-6">
                <div className="flex flex-col gap-4">
                  {navItems.map((item) => (
                    <div key={item.name}>
                      {item.isHash ? (
                        <a href={item.href} onClick={(e) => handleHashClick(e, item.href)} className="block text-lg font-serif text-ayur-950 hover:text-ayur-400 transition-colors">
                          {item.name}
                        </a>
                      ) : (
                        <Link to={item.href} onClick={() => setMenuOpen(false)} className="block text-lg font-serif text-ayur-950 hover:text-ayur-400 transition-colors">
                          {item.name}
                        </Link>
                      )}
                    </div>
                  ))}
                </div>

                <div className="h-px w-full bg-ayur-100 my-4" />

                <div className="flex flex-col gap-4">
                  <p className="text-[10px] font-bold text-ayur-400 uppercase tracking-[0.2em]">Select Language</p>
                  <div className="flex flex-wrap gap-2">
                    {LANGUAGES.map((lang) => (
                      <MobileLangButton key={lang.code} active={activeLang} busy={busy} lang={lang} onSelect={handleLanguageSelect} />
                    ))}
                  </div>
                </div>

                <div className="mt-6">
                  <a href={whatsappLink} target="_blank" rel="noopener noreferrer" onClick={() => setMenuOpen(false)} 
                    className="flex w-full items-center justify-center gap-2 px-6 py-4 bg-ayur-950 text-white text-xs uppercase tracking-widest font-medium hover:bg-ayur-800 transition-colors">
                    <WhatsAppIcon size={16} /> Consult Now
                  </a>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>
    </>
  );
}
